import React from "react";
import { AppMarkdown } from "../../../../shared/AppMarkdown/AppMarkdown";
import { progressValue } from "../../_config/okrPlansCoachConfig";
import {
  dayRange,
  reportName,
  weekday,
  rowStatement,
} from "../../_config/okrPlansCoachFormat";
import "../../_shared/OpcDoc/_opc-doc.scss";
import "./_check-in-report.scss";

// ═══════════════════════════════════════════════════════════════════════════
// The weekly AI report beside the check-in chat — laid out and styled like
// the SME weekly report (CD53, CD54: `_shared/OpcDoc`) with the BE report (2.4 / 2.7): Your Remarks (saved from the chat's
// remark card, CD19) → progress this week → executive summary → engagement →
// topics → insights → recommendations → next steps. Quiet weeks: headline,
// summary, pace, next step.
// ═══════════════════════════════════════════════════════════════════════════

// Key results and initiatives are separate groups (CD39); the objective-status
// change (and any type we don't know) follows, untitled. Order inside a group
// is the BE's.
const PROGRESS_GROUPS = [
  { type: "KR", title: "Key results" },
  { type: "INITIATIVE", title: "Initiatives" },
];

const progressGroups = (rows) => {
  const known = PROGRESS_GROUPS.map((g) => ({
    ...g,
    items: rows.filter((r) => r.target_type === g.type),
  }));
  const rest = rows.filter(
    (r) => !PROGRESS_GROUPS.some((g) => g.type === r.target_type),
  );
  return [...known, { type: "OTHER", title: "", items: rest }].filter(
    (g) => g.items.length,
  );
};

const CheckInReport = ({ report, plan, objective, owner, onPrev, onNext }) => {
  const remark = report.coach_remark;
  const eng = report.engagement || {};
  const quiet = report.kind === "QUIET";
  const progress = report.progress_this_week || [];
  return (
    <div className="project-report-main overflow-auto">
      <div className="report-header opc-report-head p-4">
        <div className="opc-report-head__title">
          <span className="fw-bold dark-grey">Weekly AI Report</span>
          <span className="light-text report-description">{objective}</span>
        </div>
        {/* CD34: one pill that never wraps. */}
        <div className="opc-report-date">
          <button
            type="button"
            className="opc-report-nav"
            aria-label="Previous report"
            disabled={!onPrev}
            onClick={onPrev}
          >
            <i className="fa-solid fa-chevron-left" aria-hidden="true" />
          </button>
          <span className="opc-report-date__label">
            <i className="fa-regular fa-calendar" aria-hidden="true" />
            <strong>{reportName(report.report_number)}</strong>
            <span>{dayRange(report.week_start, report.week_end)}</span>
          </span>
          <button
            type="button"
            className="opc-report-nav"
            aria-label="Next report"
            disabled={!onNext}
            onClick={onNext}
          >
            <i className="fa-solid fa-chevron-right" aria-hidden="true" />
          </button>
        </div>
      </div>
      <div className="report-details">
        <div className="coach-review border-bottom">
          <div className="d-flex align-items-center">
            <img src="/assets/images/user-review.svg" alt="" />
            <span className="fw-bold dark-grey small ms-2">Your Remarks</span>
            {remark?.at ? (
              <span className="light-text small ms-2">
                · {weekday(remark.at)}
              </span>
            ) : null}
          </div>
          {remark?.text ? (
            <div className="coach-remarks-main p-3 mt-3 small">
              <AppMarkdown>{remark.text}</AppMarkdown>
            </div>
          ) : (
            <div className="coach-remarks-dashed mt-3 p-3">
              <span className="small">
                Draft your remark by chatting with the assistant, then save it
                from the card. {owner} sees it on this report straight away.
              </span>
            </div>
          )}
        </div>

        {progress.length ? (
          // CD53: the SME report's "Changes this week" — same rows.
          <section className="opc-doc__section">
            <h3 className="opc-doc__h">Changes this week</h3>
            {progressGroups(progress).map((g) => (
              <div key={g.type} className="opc-doc__group">
                {g.title ? (
                  <h4 className="opc-doc__group-title">{g.title}</h4>
                ) : null}
                <ul className="opc-doc__rows">
                  {g.items.map((p, i) => (
                    <li key={`${p.target_type}-${p.target_id}`}>
                      <span className="opc-doc__item">
                        {/* The badge counts 1, 2, 3… in this list (CD40). */}
                        {g.title ? (
                          <span
                            className={`opc-num${p.target_type === "KR" ? "" : " is-plain"}`}
                          >
                            {i + 1}
                          </span>
                        ) : null}
                        <span>{rowStatement(p, plan)}</span>
                      </span>
                      <strong>
                        {progressValue(p, p.from)}
                        <i
                          className="fa-solid fa-arrow-right"
                          aria-hidden="true"
                        />
                        {progressValue(p, p.to)}
                      </strong>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        ) : null}

        {/* CD54: the SME weekly report's sections and look. */}
        {quiet ? (
          <>
            <section className="opc-doc__empty">
              <i className="fa-regular fa-moon" aria-hidden="true" />
              <strong>{report.headline || "A quiet week"}</strong>
              {report.summary ? <span>{report.summary}</span> : null}
            </section>
            {report.pace ? (
              <section className="opc-doc__section">
                <h3 className="opc-doc__h">Pace</h3>
                <p className="opc-doc__summary">{report.pace}</p>
              </section>
            ) : null}
            {report.next_step ? (
              <section className="opc-doc__section opc-doc__insights">
                <h3 className="opc-doc__h">Next step</h3>
                <p className="opc-doc__summary">{report.next_step}</p>
              </section>
            ) : null}
          </>
        ) : (
          <>
            {report.executive_summary ? (
              <section className="opc-doc__section">
                <h3 className="opc-doc__h">Executive Summary</h3>
                <p className="opc-doc__summary">{report.executive_summary}</p>
              </section>
            ) : null}

            {/* CD47 (BE FE62): three plain numbers. */}
            <section
              className="opc-doc__metrics"
              aria-label="This week in numbers"
            >
              {[
                ["Total Conversations", eng.chats_active, "chat", "chats"],
                ["Engagement Days", eng.engagement_days, "day", "days"],
                [
                  "Avg. Conversation Depth",
                  eng.avg_exchanges,
                  "exchange",
                  "exchanges",
                ],
              ].map(([label, n, one, many]) => (
                <div key={label} className="opc-doc__metric">
                  <span className="opc-doc__metric-label">{label}</span>
                  <strong>
                    {n ?? 0} {n === 1 ? one : many}
                  </strong>
                </div>
              ))}
            </section>

            {(report.topics_explored || []).length ? (
              <section className="opc-doc__section">
                <h3 className="opc-doc__h">Topics Explored</h3>
                <ul className="opc-doc__topics">
                  {report.topics_explored.map((t) => (
                    <li key={t.title} className="opc-doc-topic">
                      <div className="opc-doc-topic__head">
                        <strong>{t.title}</strong>
                        <span className="opc-doc-topic__badges">
                          {t.most_recent_topic ? (
                            <span className="opc-doc-topic__recent">
                              Most Recent
                            </span>
                          ) : null}
                          <span className="opc-doc-topic__chats">
                            <i
                              className="fa-regular fa-comments"
                              aria-hidden="true"
                            />
                            <strong>{t.number_of_chats}</strong>{" "}
                            {t.number_of_chats === 1 ? "chat" : "chats"}
                          </span>
                        </span>
                      </div>
                      <p className="opc-doc-topic__desc">{t.description}</p>
                      {(t.key_focus || []).length ? (
                        <div className="opc-doc-topic__focus">
                          <span>Key Focus:</span>
                          {t.key_focus.map((f) => (
                            <span className="opc-doc-topic__chip" key={f}>
                              {f}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {(report.coaching_insights || []).length ? (
              <section className="opc-doc__section opc-doc__insights">
                <h3 className="opc-doc__h">AI Coaching Insights</h3>
                <ul>
                  {report.coaching_insights.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
              </section>
            ) : null}

            {report.recommendations?.items?.length ? (
              <section className="opc-doc__section opc-doc__insights">
                <h3 className="opc-doc__h">Recommendations</h3>
                {report.recommendations.title ? (
                  <h4 className="opc-doc__subh">
                    {report.recommendations.title}
                  </h4>
                ) : null}
                <ul>
                  {report.recommendations.items.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
              </section>
            ) : null}

            {report.next_steps ? (
              <section className="opc-doc__section">
                <h3 className="opc-doc__h">Next steps</h3>
                <p className="opc-doc__summary">{report.next_steps}</p>
              </section>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default CheckInReport;
